import { Cached, Check, ErrorOutline, ExpandMore } from "@mui/icons-material";
import { BulkUpdateInteractionsResponse } from "../../../../types/interaction/BulkUpdate";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Stack,
  Typography,
} from "@mui/material";

interface DisplayBulkEditResponseProps {
  response: BulkUpdateInteractionsResponse;
  rerun: () => Promise<void>;
}
/**
 * Displays the results of the Bulk Update and adds a rerun functionality for failed interactions
 * @param response the response item
 * @constructor
 */
export default function DisplayBulkEditResponse({
  response,
  rerun,
}: Readonly<DisplayBulkEditResponseProps>) {
  if (!response) {
    return <></>;
  }

  const failedResults = response.results.filter(
    (result) => result.status !== "OK"
  );

  return (
    <Stack spacing={2}>
      {response.totalUpdated > 0 && (
        <Alert severity="success" icon={<Check />}>
          {failedResults.length <= 0 ? "All" : response.totalUpdated}{" "}
          interactions were updated successfully.
        </Alert>
      )}
      {failedResults.length > 0 && (
        <>
          <Alert severity="error" icon={<ErrorOutline />}>
            {failedResults.length} interactions failed to update.
          </Alert>
          <Box
            sx={{
              maxHeight: 250,
              overflowY: "auto",
              p: 2,
            }}
          >
            <Box justifyContent={"space-between"} display={"flex"} mb={2}>
              <Typography variant="h5">Error details:</Typography>

              <Button startIcon={<Cached />} onClick={() => rerun()}>
                Rerun
              </Button>
            </Box>
            {failedResults.map((item) => (
              <Accordion key={item.interactionId}>
                <AccordionSummary expandIcon={<ExpandMore />} color="red">
                  <Box display="flex" alignItems="center" gap={1}>
                    <ErrorOutline color="error" fontSize="small" />
                    <Typography variant="subtitle2">
                      Interaction ID: {item.interactionId}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography>Status: {item.status}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.errorMessage ?? "No error message provided."}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </>
      )}
    </Stack>
  );
}
